// src/Activation/Activation.jsx
import React, { useState, Fragment, useEffect } from 'react';
import { useLicense } from '../_helper/license/LicenseContext';
import { Col, Container, Form, FormGroup, Input, Label, Row } from "reactstrap";
import { Btn, H4, P, Image } from "../AbstractElements";
import logoLight from '../../src/assets/images/login/logo_white.png';
import logoDark from '../../src/assets/images/login/logo_dark.png';
import { useTranslation } from 'react-i18next';
import { ToastContainer, toast } from "react-toastify";
import { IoMdKey } from "react-icons/io";
import { useNavigate } from 'react-router-dom';
import { usePharmacy } from '../_helper/PharmacyProvider';
import axiosInstance from '../interceptor/intercetptor-inctence';

export default function Activation() {
    const [token, setToken] = useState('');
    const [loading, setLoading] = useState(false);
    const { setPharmacyPOS, pharmacyPOS } = usePharmacy();

    const {
        licenseStatus,
        licenseData,
        isValid,
        saveLicenseToken,
        decodeTokenOnly,
        doesLicenseBelongToUser,
        // clearLicense
    } = useLicense();
    const navigate = useNavigate();
    const { t } = useTranslation();

    // AUTO REDIRECT IF ALREADY VALID
    // Only redirect if license is VALID AND belongs to current user
    useEffect(() => {
        const checkAndRedirect = async () => {
            if (isValid && licenseData && pharmacyPOS?._id) {
                // Verify license belongs to current user
                const belongsToUser = doesLicenseBelongToUser(licenseData, pharmacyPOS._id);

                if (belongsToUser) {
                    console.log('License already valid → redirecting to dashboard');
                    toast.success('License already active! Redirecting...');
                    navigate(`${process.env.PUBLIC_URL}/dashboard`, { replace: true });
                }
            }
        };

        checkAndRedirect();
    }, [isValid, licenseData, pharmacyPOS?._id, navigate]);


    // const handleActivate = async () => {
    //     const inputToken = token.trim();
    //     if (!inputToken) {
    //         toast.warning('Please enter your license key');
    //         return;
    //     }

    //     try {
    //         setLoading(true);
    //         await saveLicenseToken(inputToken);

    //         // Decode and validate the token before redirecting
    //         const decodedPayload = await decodeTokenOnly();
    //         const pharmacyUser = JSON.parse(localStorage.getItem('currentPharmacyPOS'));

    //         if (!decodedPayload || !pharmacyUser) {
    //             toast.warning('Invalid license or user session');
    //             setLoading(false);
    //             return;
    //         }

    //         // Check if license belongs to current user
    //         const userBelongs = doesLicenseBelongToUser(decodedPayload, pharmacyUser._id);

    //         if (!userBelongs) {
    //             toast.warning('This license does not belong to your account');
    //             // await clearLicense();
    //             setLoading(false);
    //             return;
    //         }

    //         toast.success('License activated successfully!');
    //         // Give it a moment to save, then redirect
    //         setTimeout(() => {
    //             navigate(`${process.env.PUBLIC_URL}/dashboard`, { replace: true });
    //             setLoading(false);
    //         }, 500);

    //     } catch (err) {
    //         toast.warning('Failed to activate license');
    //         setLoading(false);
    //     }
    // };

    const handleActivate = async () => {
        const inputToken = token.trim();
        if (!inputToken) {
            toast.warning('Please enter your license key');
            return;
        }
        setLoading(true)
        try {
            const firstName = pharmacyPOS?.contact_person?.first_name;
            const lastName = pharmacyPOS?.contact_person?.last_name;
            const payload = {
                licenseKey: inputToken,
                user: {
                    id: pharmacyPOS._id,
                    name: firstName + " " + lastName,
                }
            }
            const response = await axiosInstance.post(`healthcare_providers/auth/activateLicenseKey`, payload);

            if (response.data.status === 'success') {
                // Save the token
                await saveLicenseToken(response.data.data.data.token);

                // Verify it belongs to user before redirecting
                // const decodedPayload = await decodeTokenOnly();
                // const belongsToUser = doesLicenseBelongToUser(decodedPayload, pharmacyPOS._id);

                // if (!belongsToUser) {
                //     toast.warning('This license does not belong to your account');
                //     setToken('');
                //     return;
                // }

                toast.success(response.data.data.message);

                setTimeout(() => {
                    navigate(`${process.env.PUBLIC_URL}/dashboard`, { replace: true });
                    setLoading(false);
                }, 500);
            }
        } catch (err) {
            console.log(err.response?.data?.message || err.message || 'Failed to activate license');
            // toast.warning(err.response?.data?.message || err.message || 'Failed to activate license');
        } finally {
            setLoading(false);
        }
    };

    const [containerHeight, setContainerHeight] = useState(window.innerHeight);
    useEffect(() => {
        const handleResize = () => {
            setContainerHeight(window.innerHeight);
        };

        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    // Helper to format expiry date
    const formatExpiry = () => {
        if (!licenseData?.expiresAt) return null;

        const date = new Date(Number(licenseData.expiresAt));

        return date.toLocaleString("en-GB", {
            timeZone: "UTC",
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "numeric",
            minute: "numeric",
            second: "numeric",
            hour12: true,
        });
    };

    // MESSAGE COMPONENT — RIGHT SIDE CARD
    const renderStatusMessage = () => {
        switch (licenseStatus) {
            case 'expired':
                return (
                    <div className="alert alert-warning mt-4 p-4 border-left-warning">
                        <h5>License Expired</h5>
                        <p className="mb-2">
                            Your license expired on: <strong>{formatExpiry()}</strong>
                        </p>
                        <p className="mb-0">
                            Please contact support or renew your subscription to continue using the application.
                        </p>
                    </div>
                );

            case 'time-tampered':
                return (
                    <div className="alert alert-danger mt-4 p-4 border-left-danger">
                        <h5>Security Alert</h5>
                        <p className="mb-2">
                            <strong>System clock manipulation detected!</strong>
                        </p>
                        <p className="mb-0">
                            Your system date/time was changed to bypass license expiry.<br />
                            Please set the correct date and time to continue.
                        </p>
                    </div>
                );

            case 'invalid':
                return (
                    <div className="alert alert-warning mt-4 p-2">
                        <h5>Invalid License</h5>
                        <p>The license key is invalid, corrupted, or has been tampered with.</p>
                    </div>
                );

            // case 'none':
            //     return (
            //         <div className="alert alert-info mt-4 p-4">
            //             <h5>No License Found</h5>
            //             <p>Please enter your license key to activate the application.</p>
            //         </div>
            //     );

            default:
                return null;
        }
    };

    const navigateToLogin = () => {
        localStorage.removeItem("pharmacyPOSAuth");
        localStorage.removeItem("currentPharmacyPOS");
        localStorage.setItem("authenticated", false);
        setPharmacyPOS(null);
        navigate(`${process.env.PUBLIC_URL}/login`, { replace: true });
    }


    return (
        // <div className="activation-container"
        //     style={{
        //         display: "flex",
        //         justifyContent: "center",
        //         alignItems: "center",
        //         height: "100vh",
        //         background: "#f3f4f6"
        //     }}
        // >
        //     <div style={{
        //         background: "#fff",
        //         padding: "2rem",
        //         width: "400px",
        //         borderRadius: "10px",
        //         boxShadow: "0px 4px 15px rgba(0,0,0,0.1)"
        //     }}>
        //         <h2>Activate Your Application</h2>
        //         <p>Please enter the license key provided to you.</p>

        //         <input
        //             type="text"
        //             placeholder="Enter License Key"
        //             value={token} onChange={e => setToken(e.target.value)}
        //             style={{ width: "100%", padding: "10px", marginTop: "15px", borderRadius: "8px" }}
        //         />

        //         <button
        //             onClick={handleActivate} disabled={loading}
        //             style={{
        //                 marginTop: "20px",
        //                 width: "100%",
        //                 padding: "12px",
        //                 background: "#2563eb",
        //                 color: "white",
        //                 border: "none",
        //                 borderRadius: "8px",
        //                 cursor: "pointer"
        //             }}
        //         >
        //             {loading ? "Activating..." : "Activate"}
        //         </button>
        //     </div>
        // </div>

        <Fragment>
            <div className="login-page">
                <Container fluid={false} style={{ paddingTop: '1rem', paddingBottom: '0.5rem' }}>
                    <div className="card">
                        <Row className="m-0">
                            <Col
                                xs="6"
                                className="b-center bg-size d-none d-md-flex flex-column"
                                style={{
                                    backgroundImage: `url(${require('../assets/images/login/login_left_img.png')})`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    // borderTopLeftRadius: '15px',
                                    // borderBottomLeftRadius: '15px',
                                    // padding: '20px',
                                    color: '#1B245B', // Ensures text is white on the dark background
                                }}
                            >
                                <div style={{ marginLeft: "10%" }}>
                                    <Image
                                        attrImage={{
                                            className: 'login-logo-style',
                                            src: `${logoDark}`,
                                            alt: 'CLINICDOTS Logo',
                                        }}
                                    />
                                    <h3 style={{ fontWeight: 'bold', marginBottom: '0px', letterSpacing: '2px' }}>CLINICDOTS</h3>
                                    <h3 style={{ fontWeight: 'bold', marginBottom: '20px', letterSpacing: '3px' }}>PHARMACY</h3>
                                    <span>{t("Innovating Healthcare for India.")}</span>
                                </div>
                            </Col>

                            <Col xs="12" md="6"
                                style={{
                                    backgroundImage: `url(${require('../assets/images/login/login_right_img.png')})`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    // borderTopLeftRadius: '15px',
                                    // borderBottomLeftRadius: '15px',
                                    minHeight: `${containerHeight * 0.94}px`,
                                    display: 'flex',
                                    flexDirection: 'column'
                                }}>
                                {/* STATUS MESSAGES */}
                                {renderStatusMessage()}
                                <div className='login-card' style={{ flex: '1' }}>
                                    <div className="login-form-responsive">
                                        <Form className="theme-form login-form">
                                            {/* This image will only be visible on mobile screens */}
                                            <Image
                                                attrImage={{
                                                    className: 'login-dark-logo-style d-block d-md-none',
                                                    src: `${logoDark}`,
                                                    alt: 'CLINICDOTS Logo',
                                                }}
                                            />
                                            <H4><strong className="mb-3">{t("Activate Your Application")}</strong></H4>
                                            {/* ACTIVATION FORM */}
                                            {['none', 'invalid', 'expired', 'time-tampered'].includes(licenseStatus) && (
                                                <>
                                                    <P>{t("Please enter the license key provided to you.")}</P>

                                                    <FormGroup className="position-relative">
                                                        {/* <Label className="col-form-label">{t("License Key")}</Label> */}
                                                        <div className="position-relative">
                                                            <IoMdKey className="position-absolute" style={{ left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#000', fontSize: "x-large" }} />
                                                            {/* <i className="fa fa-envelope-o position-absolute" style={{ left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#000', fontSize: "large" }}></i> */}
                                                            <Input
                                                                className="form-control pl-5"
                                                                type="text"
                                                                placeholder={t("Enter License Key")}
                                                                onChange={e => setToken(e.target.value)}
                                                                value={token}
                                                                style={{ paddingLeft: '40px' }}
                                                            />
                                                        </div>
                                                        {/* {emailError && <div className="text-danger">{emailError}</div>} */}
                                                    </FormGroup>
                                                </>
                                            )}

                                            {/* <p style={{ textAlign: "end" }}><strong style={{ cursor: "pointer" }} onClick={forgotPassword}>{t("Forgot Password ?")}</strong></p> */}
                                            <div className="position-relative form-group mb-0">
                                                <Btn attrBtn={{ type: "submit", color: "primary", className: "d-block w-100 login-button", onClick: handleActivate, disabled: loading }}>{loading ? t("Activating...") : t("Activate")}</Btn>
                                            </div>
                                        </Form>
                                        <p className='mt-2' style={{ textAlign: "end" }}><strong style={{ cursor: "pointer" }} onClick={navigateToLogin}>{t("Back to login")}</strong></p>
                                    </div>
                                </div>
                                <div className="mx-3" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <small className="my-3">
                                        {t("All rights reserved Copyright ©")} {new Date().getFullYear()} {t("CLINICDOTS HEALTHCARE.")}
                                    </small>
                                </div>
                            </Col>
                        </Row>
                    </div>
                </Container>
            </div>
            <ToastContainer />
        </Fragment>
    );
}
